import { useState, useCallback } from 'react'
import { Toast } from '../toast'
import { encodeFilePathForUrl } from './utils'

interface DownloadState {
  isDownloading: boolean
  downloadProgress: number
  activeFileId: string | number | null
  activeFileIndex: number | null
}

interface UseDownloadFileReturn extends DownloadState {
  downloadFile: (
    fileName: string,
    filePath: string,
    fileId?: string | number | null,
    index?: number | null
  ) => Promise<void>
}

/**
 * Hook that handles file downloading with streaming progress tracking.
 *
 * Replaces duplicated download logic (~90 lines each) in SubInfo and Files components.
 *
 * Usage:
 * ```ts
 * const { isDownloading, downloadProgress, activeFileId, downloadFile } = useDownloadFile()
 * downloadFile('report.pdf', '/uploads/report.pdf', fileId, 0)
 * ```
 */
export function useDownloadFile(): UseDownloadFileReturn {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [activeFileId, setActiveFileId] = useState<string | number | null>(null)
  const [activeFileIndex, setActiveFileIndex] = useState<number | null>(null)

  const downloadFile = useCallback(
    async (
      fileName: string,
      filePath: string,
      fileId?: string | number | null,
      index?: number | null
    ): Promise<void> => {
      setIsDownloading(true)
      setActiveFileId(fileId ?? null)
      setActiveFileIndex(index ?? null)
      setDownloadProgress(0)

      try {
        const encodedFilePath = encodeFilePathForUrl(filePath)
        const response = await fetch(encodedFilePath, {
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          method: 'GET',
        })

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`)
        }

        const contentLength = response.headers.get('Content-Length')
        const totalBytes = contentLength ? parseInt(contentLength, 10) : 0
        let receivedLength = 0

        const body = response.body
        if (!body) {
          throw new Error('Response body is not available')
        }

        const reader = body.getReader()
        const chunks: BlobPart[] = []

        for (;;) {
          const { done, value } = await reader.read()
          if (done) break

          chunks.push(value)
          receivedLength += value.length

          if (totalBytes > 0) {
            setDownloadProgress((receivedLength / totalBytes) * 100)
          }
        }

        const blob = new Blob(chunks)
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()

        // Clean up
        window.URL.revokeObjectURL(downloadUrl)
        link.remove()
      } catch (error: any) {
        const message =
          error instanceof Error ? error.message : 'Download failed'
        Toast({
          type: 'error',
          message,
        })
      } finally {
        setIsDownloading(false)
        setActiveFileId(null)
        setActiveFileIndex(null)
        setDownloadProgress(0)
      }
    },
    []
  )

  return {
    isDownloading,
    downloadProgress,
    activeFileId,
    activeFileIndex,
    downloadFile,
  }
}
